import React, { useEffect, useState } from "react";
import { getMerchant } from '../../helpers/ReduxHelpers';
import { apiConstants, getGeneric, postGeneric } from '../../common/constants';
import { useForm } from "react-hook-form";
import { Box, Button, FormControlLabel, Grid, Switch, Tooltip, Typography } from "@mui/material";
import { FormProvider, RHFTextField } from "../../common/hook-form";
import { LoadingButton } from "@mui/lab";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup'
import Loader from "../../common/Loader"
import { useSnackbar } from "notistack";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import ConfirmDialog from "../../common/ConfirmDialog";
import { primaryBtnSx, filterBarSx } from '../../styles/patterns';
import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, SUBTLE } from '../../styles/foundation/colors';
import { primaryTextSx, cellStackSx } from '../orders/list2/cellStyles';

const MisConfig = () => {
    const merchant = getMerchant();
    const merchantId = merchant.id ? merchant.id : ''
    const { enqueueSnackbar } = useSnackbar();
    const [isLoadingMIS, setLoadingMIS] = useState(false);
    const [isMISEnabled, setMISEnabled] = useState(false);
    const [isMonthlyMISEnabled, setMonthlyMISEnabled] = useState(false);
    const [merchantConfig, setMerchantConfig] = useState();
    const [reload, setReload] = useState(0);
    const [deleteData, setDeleteData] = useState();
    const [sizePerPage, setSizePerPage] = useState(10);
    const [page, setPage] = useState(0);
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        type: null,
        message: '',
        disableButtons: false
    });

    const columns = [
        {
            field: 'emails',
            headerAlign: "left",
            hideSortIcons: true,
            headerName: 'Email ID',
            flex: 1.2,
            minWidth: 250,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Box sx={cellStackSx}><Typography sx={primaryTextSx}>{row?.email}</Typography></Box>
                )
            },
        },
        {
            field: 'action',
            headerAlign: "left",
            hideSortIcons: true,
            headerName: 'Action',
            flex: 0.5,
            minWidth: 120,
            renderCell: (params) => {
                const { row } = params;
                return (
                    <Button
                        disabled={!isMISEnabled && !isMonthlyMISEnabled}
                        onClick={() => handleCancelOrder(row)}
                        color="error"
                        variant="outlined"
                        size="small"
                    >
                        Delete
                    </Button>
                );
            },
        },
    ];
    const emailListRegExp = /^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,}\s*,\s*)*[\w+-.%]+@[\w-.]+\.[A-Za-z]{2,}$/

    const methodsMIS = useForm({
        resolver: yupResolver(
            Yup.object().shape({
                emailMIS: Yup.string()
                    .matches(emailListRegExp, { message: 'Invalid email format', excludeEmptyString: true })
                    .required("Email is required!"),
            })
        ),
        defaultValues: {
            emailMIS: '',
        }
    })
    const { handleSubmit: handleSubmitFormMIS, reset } = methodsMIS


    const postMerchantConfig = (payload) => {
        postGeneric(`${apiConstants.MERCHANT_DETAILS}/${merchantId}/mis`, payload).then(response => {
            enqueueSnackbar('Configuration updated successfully', { variant: 'success' })
            setReload(reload => reload + 1)
            reset()
            setLoadingMIS(false)
        })
            .catch((err) => {
                console.error(err);
                setLoadingMIS(false);
                
                // Handle specific error cases
                if (err.response?.status === 400) {
                    if (err.response?.data?.message?.includes('recievedByEmail is required')) {
                        enqueueSnackbar('Please add at least one email address before enabling emails', { variant: 'error' });
                    } else {
                        enqueueSnackbar(err.response?.data?.message || 'Invalid request. Please check your configuration.', { variant: 'error' });
                    }
                } else {
                    enqueueSnackbar('Failed to update configuration. Please try again.', { variant: 'error' });
                }
            })
    }

    const handleConfirmDialog = (type, message, disableButtons = false) => {
        setConfirmDialog({
            open: true,
            type,
            message,
            disableButtons
        });
    };

    const handleConfirmAction = () => {
        switch (confirmDialog.type) {
            case 'delete':
                handleDelete();
                break;
            case 'dailySwitch':
                toggleDailyEmail();
                break;
            case 'monthlySwitch':
                toggleMonthlyEmail();
                break;
            default:
                break;
        }
        setConfirmDialog({ ...confirmDialog, open: false });
    };

    const handleCancelOrder = (data) => {
        setDeleteData(data);
        handleConfirmDialog(
            'delete',
            merchantConfig?.misConfig?.recievedByEmail?.length === 1 
                ? "Please disable via Toggle Switch" 
                : "Are you sure you want to delete ?",
            merchantConfig?.misConfig?.recievedByEmail?.length === 1
        );
    };

    const handleDelete = () => {
        const index = merchantConfig?.misConfig?.recievedByEmail.indexOf(deleteData.email);
        if (index !== -1) {
            merchantConfig?.misConfig?.recievedByEmail.splice(index, 1);
        }
        const payload = {
            "dailyEnabled": true,
            "recievedByEmail": [...merchantConfig?.misConfig?.recievedByEmail],
            "recievedByBccEmail": ["ProshipTechTeam@prozo.com"]
        }
        postMerchantConfig(payload)
    }


    const onSubmitMIS = (data) => {
        setLoadingMIS(true);
        const existingEmails = merchantConfig?.misConfig?.recievedByEmail ?? [];
        const emailsMISArray = data.emailMIS.split(',').map(email => email.trim());
        const payload = {
            "dailyEnabled": true,
            "recievedByEmail": [...existingEmails, ...emailsMISArray],
            "recievedByBccEmail": ["ProshipTechTeam@prozo.com"]
        }
        postMerchantConfig(payload)
    }


    useEffect(() => {
        setLoadingMIS(true)
        getGeneric(`${apiConstants.MERCHANT_DETAILS}/${merchantId}`).then((res) => {
            setMerchantConfig(res.data);
            setLoadingMIS(false)
        }).catch((err) => {
            console.log(err)
            setLoadingMIS(false)
        })
    }, [merchantId, reload])

    const toggleDailyEmail = (e) => {
        setLoadingMIS(true);
        const payload = {
            monthlyEnabled: merchantConfig?.misConfig?.monthlyEnabled,
            dailyEnabled: !merchantConfig?.misConfig?.dailyEnabled,
            recievedByEmail: merchantConfig?.misConfig?.recievedByEmail,
            recievedByBccEmail: ["ProshipTechTeam@prozo.com"]

        }
        postGeneric(`${apiConstants.MERCHANT_DETAILS}/${merchantId}/mis`, payload).then(response => {
            enqueueSnackbar('Daily email configuration updated successfully', { variant: 'success' });
            reset()
            setReload(reload => reload + 1)
            setLoadingMIS(false)
        })
            .catch((err) => {
                console.error(err);
                setLoadingMIS(false);
                
                // Handle specific error cases
                if (err.response?.status === 400) {
                    if (err.response?.data?.message?.includes('recievedByEmail is required')) {
                        enqueueSnackbar('Please add at least one email address before enabling emails', { variant: 'error' });
                    } else {
                        enqueueSnackbar(err.response?.data?.message || 'Invalid request. Please check your configuration.', { variant: 'error' });
                    }
                } else {
                    enqueueSnackbar('Failed to update email configuration. Please try again.', { variant: 'error' });
                }
            })
    }
    const toggleMonthlyEmail = (e) => {
        setLoadingMIS(true);
        const payload = {
            monthlyEnabled: !merchantConfig?.misConfig?.monthlyEnabled,
            dailyEnabled: merchantConfig?.misConfig?.dailyEnabled,
            recievedByEmail: merchantConfig?.misConfig?.recievedByEmail,
            recievedByBccEmail: ["ProshipTechTeam@prozo.com"]

        }
        postGeneric(`${apiConstants.MERCHANT_DETAILS}/${merchantId}/mis`, payload).then(response => {
            enqueueSnackbar('Monthly email configuration updated successfully', { variant: 'success' });
            reset()
            setReload(reload => reload + 1)
            setLoadingMIS(false)
        })
            .catch((err) => {
                console.error(err);
                setLoadingMIS(false);
                
                // Handle specific error cases
                if (err.response?.status === 400) {
                    if (err.response?.data?.message?.includes('recievedByEmail is required')) {
                        enqueueSnackbar('Please add at least one email address before enabling emails', { variant: 'error' });
                    } else {
                        enqueueSnackbar(err.response?.data?.message || 'Invalid request. Please check your configuration.', { variant: 'error' });
                    }
                } else {
                    enqueueSnackbar('Failed to update email configuration. Please try again.', { variant: 'error' });
                }
            })
    }

    useEffect(() => {
        setMISEnabled(merchantConfig?.misConfig?.dailyEnabled)
    }, [merchantConfig?.misConfig?.dailyEnabled])

    useEffect(() => {
        setMonthlyMISEnabled(merchantConfig?.misConfig?.monthlyEnabled)
    }, [merchantConfig?.misConfig?.monthlyEnabled])

    const handleSwitchConfirmation = () => {
        // Check if trying to enable daily email but no emails exist
        if (!merchantConfig?.misConfig?.dailyEnabled && (!merchantConfig?.misConfig?.recievedByEmail || merchantConfig?.misConfig?.recievedByEmail.length === 0)) {
            enqueueSnackbar('Please add at least one email address before enabling daily emails', { variant: 'error' });
            return;
        }
        handleConfirmDialog('dailySwitch', 'Are you sure you want to change the email configuration?');
    };

    const handleMonthlySwitchConfirmation = () => {
        // Check if trying to enable monthly email but no emails exist
        if (!merchantConfig?.misConfig?.monthlyEnabled && (!merchantConfig?.misConfig?.recievedByEmail || merchantConfig?.misConfig?.recievedByEmail.length === 0)) {
            enqueueSnackbar('Please add at least one email address before enabling monthly emails', { variant: 'error' });
            return;
        }
        handleConfirmDialog('monthlySwitch', 'Are you sure you want to change the email configuration?');
    };

    return (
        <>
            {(isLoadingMIS) && <Loader />}
            <ConfirmDialog
                title=''
                open={confirmDialog.open}
                disableButtons={confirmDialog.disableButtons}
                setOpen={(open) => setConfirmDialog({ ...confirmDialog, open })}
                onConfirm={handleConfirmAction}
            >
                {confirmDialog.message}
            </ConfirmDialog>
            <Grid container spacing={2}>
                <Grid item xs={12} md={12}>
                    <Box sx={filterBarSx}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.semibold, color: NAVY }}>MIS Email Configuration</Typography>
                                <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, mt: 0.5 }}>Configure email recipients for daily and monthly MIS reports</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Tooltip title={isMISEnabled ? "Disable Daily Email" : "Enable Daily Email"}>
                                    <FormControlLabel
                                        control={<Switch checked={isMISEnabled} onChange={(e) => handleSwitchConfirmation(e)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' } }} />}
                                        label={<Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: isMISEnabled ? '#4caf50' : SUBTLE }}>{isMISEnabled ? 'Daily On' : 'Daily Off'}</Typography>}
                                        labelPlacement="start"
                                    />
                                </Tooltip>
                                <Tooltip title={isMonthlyMISEnabled ? "Disable Monthly Email" : "Enable Monthly Email"}>
                                    <FormControlLabel
                                        control={<Switch checked={isMonthlyMISEnabled} onChange={(e) => handleMonthlySwitchConfirmation(e)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' } }} />}
                                        label={<Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, color: isMonthlyMISEnabled ? '#4caf50' : SUBTLE }}>{isMonthlyMISEnabled ? 'Monthly On' : 'Monthly Off'}</Typography>}
                                        labelPlacement="start"
                                    />
                                </Tooltip>
                            </Box>
                        </Box>
                        <FormProvider methods={methodsMIS} onSubmit={handleSubmitFormMIS(onSubmitMIS)}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <RHFTextField
                                        name="emailMIS"
                                        label="Add Emails *"
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <LoadingButton isLoading={isLoadingMIS}
                                        type="submit"
                                        variant="contained"
                                        size="small"
                                        sx={primaryBtnSx}
                                    >Add</LoadingButton>
                                </Grid>
                            </Grid>
                        </FormProvider>
                    </Box>
                    <Grid item xs={12} sx={{ mt: 1 }}>
                        <ProzoDataGrid
                            columns={columns}
                            autoHeight={true}
                            rows={(merchantConfig && merchantConfig?.misConfig?.recievedByEmail?.length > 0)
                                ? merchantConfig?.misConfig?.recievedByEmail.map((email, index) => ({ id: index, email }))
                                : []
                            }
                            sizePerPage={sizePerPage}
                            setSizePerPage={setSizePerPage}
                            setPage={setPage}
                            page={page}
                            paginationMode='client'
                            pagination={true}
                            rowCount={merchantConfig?.misConfig?.recievedByEmail?.length || 0}
                            getRowId={(row) => row.id}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default MisConfig;
